import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { FatalError } from "@/components/FatalError";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useSocialMediaPlatforms } from "@/hooks/useSocialMediaPlatforms";
import { useReplaceUserSocials } from "@/hooks/useReplaceUserSocials";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { SocialMediaHandleInput } from "@/__generated__/graphql";

type SocialsFormData = Record<string, string>;

export const EditSocials = () => {
  const { t } = useTranslation();
  const {
    profile,
    loading: profileLoading,
    error: profileError,
  } = useMyProfile();

  const {
    platforms,
    loading: platformsLoading,
    error: platformsError,
  } = useSocialMediaPlatforms();

  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  const [replaceUserSocials, { loading: updateLoading }] =
    useReplaceUserSocials({
      onError: (error) => {
        let errorMessage = t("global.unexpectedError");

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        toast.error(t("global.unexpectedError"), {
          description: errorMessage,
        });
      },
    });

  const defaultValues = useMemo(() => {
    const values: SocialsFormData = {};

    platforms.forEach((platform) => {
      const existingSocial = profile?.socials.find(
        (social) => social.platform.id === platform.id,
      );
      values[platform.id] = existingSocial?.handle || "";
    });

    return values;
  }, [platforms, profile?.socials]);

  const {
    handleSubmit,
    register,
    reset,
    formState: { isDirty, errors },
    clearErrors,
  } = useForm<SocialsFormData>({
    defaultValues,
    values: Object.keys(defaultValues).length > 0 ? defaultValues : undefined,
  });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !confirmedNavigation &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowUnsavedChangesDialog(true);
    }
  }, [blocker.state]);

  const handleUnsavedChangesLeave = () => {
    setShowUnsavedChangesDialog(false);
    setConfirmedNavigation(true);
    blocker.proceed?.();
  };

  const handleUnsavedChangesStay = () => {
    setShowUnsavedChangesDialog(false);
    blocker.reset?.();
  };

  const onSubmit = async (data: SocialsFormData) => {
    if (!profile?.id) return;

    const socials: SocialMediaHandleInput[] = Object.entries(data)
      .filter(([, handle]) => handle.trim() !== "")
      .map(([platformId, handle]) => ({
        platformId,
        handle: handle.trim(),
      }));

    const result = await replaceUserSocials({
      userId: profile.id,
      socials,
    });

    if (result?.errors?.length) {
      toast.error(t("global.unexpectedError"), {
        description: result.errors[0].message,
      });
    } else {
      toast.success(t("global.save"));
      reset(data);
    }
  };

  const handleCancel = () => {
    reset(defaultValues);
    clearErrors();
  };

  if (profileError || platformsError) {
    return <FatalError />;
  }

  const loading = profileLoading || platformsLoading;

  if (loading) {
    return (
      <Card className="w-[760px]">
        <CardHeader>
          <CardTitle className="text-xl">
            {t("manageProfile.editSocials.title")}
          </CardTitle>
          <CardDescription>
            {t("manageProfile.editSocials.subTitle")}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6 p-10 px-8">
          {[...Array(4)].map((_unused, index) => (
            <div key={index} className="flex flex-col gap-2">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </CardContent>

        <CardFooter className="justify-end gap-3 border-t px-6 py-4">
          <Skeleton className="h-10 w-16" />
        </CardFooter>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-[760px]">
        <CardHeader>
          <CardTitle className="text-xl">
            {t("manageProfile.editSocials.title")}
          </CardTitle>
          <CardDescription>
            {t("manageProfile.editSocials.subTitle")}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="flex flex-col gap-6 p-10 px-8">
            {platforms.map((platform) => (
              <div key={platform.id} className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  {platform.name}
                </label>
                <input
                  type="text"
                  placeholder={platform.name}
                  {...register(platform.id)}
                  className={`h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] ${
                    errors[platform.id]
                      ? "outline-destructive"
                      : "outline-input"
                  }`}
                />
                {errors[platform.id] && (
                  <p className="text-xs text-destructive">
                    {errors[platform.id]?.message}
                  </p>
                )}
              </div>
            ))}
          </CardContent>

          <CardFooter className="justify-between border-t px-6 py-4">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
            >
              {t("global.cancel")}
            </button>
            <button
              type="submit"
              disabled={!isDirty || updateLoading}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("global.save")}
            </button>
          </CardFooter>
        </form>
      </Card>

      {/* Unsaved Changes Dialog */}
      <AlertDialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("manageProfile.editSocials.unsavedChangesAlert.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("manageProfile.editSocials.unsavedChangesAlert.message")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleUnsavedChangesStay}>
              {t("global.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleUnsavedChangesLeave}>
              {t("manageProfile.editSocials.unsavedChangesAlert.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
